
import {
  computed, defineComponent, nextTick, onBeforeUnmount, PropType, ref, watch,
} from 'vue'
import axios from 'axios'
import { Message } from '@/types/webinar.d'
import IconSend from '@/vue/icons/IconSend.vue'
import IconMessage from '@/vue/icons/IconMessage.vue'
import { useI18n } from 'vue-i18n'
import periodic, { CancelFunc } from '@/scripts/periodic'
import { request } from '@/scripts/combinedRequest'
import sleep from '@/scripts/sleep'
import ChatMessage from './ChatMessage.vue'

const GETTER_PERIOD = 5000

export default defineComponent({
  name: 'Chat',
  props: {
    scheduleId: {
      type: Number as PropType<number>,
      required: true,
    },
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    started: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    archived: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChatMessage,
    IconSend,
    IconMessage,
  },
  setup(props) {
    const { t } = useI18n()

    const messages = ref<Message[]>([])
    const messageIdList = computed(() => messages.value.map((m) => m.id))
    const lastTiming = computed(() => {
      const l = messages.value.length
      return l ? messages.value[l - 1].timing : 0
    })

    const messagesBlock = ref<HTMLElement>()

    const openChat = ref(false)
    const sending = ref(false)
    const message = ref('')

    const editable = ref<HTMLElement>()

    const messageLimit = 255
    watch(message, () => {
      if (message.value.length > 255) {
        message.value = message.value.slice(0, 255)
        if (editable.value) {
          editable.value.innerText = message.value
          if (editable.value?.lastChild) {
            const range = document.createRange()
            range.setStart(editable.value.lastChild, (editable.value.lastChild as Text).length)
            const selection = document.getSelection()
            selection?.removeAllRanges()
            selection?.addRange(range)
          }
        }
      }
    })
    const messageCounter = computed(() => messageLimit - message.value.length)

    // MESSAGES REMOVER
    onBeforeUnmount(periodic(15000, async () => {
      const getIdList = await request<number[]>('messages_id')
      messages.value = messages.value.filter((m: Message) => getIdList.includes(m.id))
    }))

    // MESSAGES GETTER
    let cancelGetter: CancelFunc

    function runGetter() {
      if (cancelGetter) {
        cancelGetter()
      }
      cancelGetter = periodic(GETTER_PERIOD, async () => {
        const timing = lastTiming.value
        let newMessages = await request<Message[]>('messages', timing)
        newMessages = newMessages
          .filter((m: Message) => !messageIdList.value?.includes(m.id))
          .map((m: Message) => Object.assign(m, { timestamp: new Date(m.timestamp) }))
        if (newMessages.length) {
          const delta = newMessages[newMessages.length - 1].timing - newMessages[0].timing
          if (delta >= (GETTER_PERIOD / 1000)) {
            messages.value.push(...newMessages)
            await nextTick(() => {
              if (messagesBlock.value) {
                messagesBlock.value.scrollTo(0, messagesBlock.value.scrollHeight)
              }
            })
          } else {
            nextTick(async () => {
              let previousTiming = 0
              for (let i = 0; i < newMessages.length; i++) {
                const m = newMessages[i]
                if (previousTiming) {
                  const d = m.timing - previousTiming
                  if (d >= 0) {
                    // eslint-disable-next-line no-await-in-loop
                    await sleep(d * 1000)
                  }
                }
                previousTiming = m.timing
                messages.value.push(m)
                // eslint-disable-next-line no-await-in-loop
                await nextTick(() => {
                  if (messagesBlock.value) {
                    messagesBlock.value.scrollTo(0, messagesBlock.value.scrollHeight)
                  }
                })
              }
            })
          }
        }
      })
    }

    runGetter()

    onBeforeUnmount(() => {
      if (cancelGetter) {
        cancelGetter()
      }
    })

    return {
      t,

      openChat,
      messages,
      sending,
      message,
      messagesBlock,
      editable,
      messageCounter,

      onInput() {
        message.value = editable.value ? editable.value.innerText : ''
      },
      async sendMessage() {
        if (!sending.value) {
          const m = message.value.trim()
          if (m) {
            sending.value = true
            await axios.post(`/messages/${props.scheduleId}`, { message: m })
            message.value = ''
            if (editable.value) {
              editable.value.innerText = ''
            }
            runGetter()
            await nextTick(() => {
              sending.value = false
              if (editable.value) {
                editable.value.focus()
              }
            })
          }
        }
      },
      removeMessage(id: number) {
        messages.value = messages.value.filter((m: Message) => id !== m.id)
      },

      inputClass: computed(() => [
        'webinar__chat__input',
        {
          'webinar__chat__input--not-empty': !!message.value,
          'webinar__chat__input--sending': sending.value,
        },
      ]),
    }
  },
})
